import { BatchActionBar, SelectModeButton } from "@/components/BatchActionBar";
import { ProcessingStatus } from "@/components/ProcessingStatus";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "@/components/ui/table";
import { AIService } from "@/gen/omnios/v1/ai_connect";
import { MediaService } from "@/gen/omnios/v1/media_connect";
import { transport } from "@/lib/grpc";
import { useBreadcrumbs } from "@/lib/use-breadcrumbs";
import { useSlashFocus } from "@/lib/use-slash-focus";
import { createClient } from "@connectrpc/connect";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { FileText, FileUp, Inbox, Loader2, Search } from "lucide-react";
import { useCallback, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

export const Route = createFileRoute("/settings/knowledge")({
	component: KnowledgeSettingsPage,
});

const aiClient = createClient(AIService, transport);
const mediaClient = createClient(MediaService, transport);

function KnowledgeSettingsPage() {
	const { t } = useTranslation("settings");

	useBreadcrumbs([
		{ label: t("breadcrumb_settings"), href: "/settings" },
		{ label: t("knowledge_breadcrumb") },
	]);

	const queryClient = useQueryClient();
	const navigate = useNavigate();
	const [search, setSearch] = useState("");
	const searchRef = useRef<HTMLInputElement>(null);
	const clearSearch = useCallback(() => setSearch(""), []);
	useSlashFocus(searchRef, clearSearch);
	const [selectMode, setSelectMode] = useState(false);
	const [selected, setSelected] = useState<Set<string>>(new Set());

	const { data, isLoading } = useQuery({
		queryKey: ["knowledge-documents"],
		queryFn: () =>
			aiClient.listKnowledgeDocuments({
				page: {
					pageSize: 100,
					pageToken: "",
				},
			}),
		refetchInterval: (query) => {
			const docs = query.state.data?.documents ?? [];
			return docs.some(
				(d) => d.status === "pending" || d.status === "processing",
			)
				? 3000
				: 30000;
		},
	});

	const uploadMut = useMutation({
		mutationFn: async (file: File) => {
			const buffer = await file.arrayBuffer();
			const media = await mediaClient.uploadMedia({
				data: new Uint8Array(buffer),
				filename: file.name,
				mimeType: file.type || "application/octet-stream",
			});
			return aiClient.uploadKnowledgeDocument({
				mediaId: media.id,
				title: file.name,
				filename: file.name,
				mimeType: file.type || "application/octet-stream",
			});
		},
		onSuccess: () =>
			queryClient.invalidateQueries({
				queryKey: ["knowledge-documents"],
			}),
	});

	const deleteMut = useMutation({
		mutationFn: async (ids: string[]) => {
			for (const id of ids) {
				await aiClient.deleteKnowledgeDocument({
					id,
				});
			}
		},
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: ["knowledge-documents"],
			});
			setSelected(new Set());
			setSelectMode(false);
		},
	});

	const handleFileSelect = useCallback(() => {
		const input = document.createElement("input");
		input.type = "file";
		input.accept = ".pdf,.xlsx,.xls,.docx,.csv,.txt,.md";
		input.multiple = true;
		input.onchange = (e) => {
			const files = (e.target as HTMLInputElement).files;
			if (files) {
				for (const file of Array.from(files)) {
					uploadMut.mutate(file);
				}
			}
		};
		input.click();
	}, [uploadMut]);

	const allDocs = data?.documents ?? [];
	const searchLower = search.toLowerCase();
	const documents = search
		? allDocs.filter(
				(d) =>
					d.title.toLowerCase().includes(searchLower) ||
					d.filename.toLowerCase().includes(searchLower),
			)
		: allDocs;

	const toggleSelect = (id: string) => {
		setSelected((prev) => {
			const next = new Set(prev);
			if (next.has(id)) next.delete(id);
			else next.add(id);
			return next;
		});
	};

	return (
		<div className="space-y-4">
			{/* Toolbar */}
			<div className="flex items-center gap-3">
				<div className="relative flex-1 max-w-sm">
					<Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
					<Input
						ref={searchRef}
						value={search}
						onChange={(e) => setSearch(e.target.value)}
						placeholder={t("knowledge_search_placeholder")}
						className="pl-9 h-9"
					/>
				</div>
				<div className="flex items-center gap-2 ml-auto">
					<SelectModeButton
						selectMode={selectMode}
						onToggle={() => {
							setSelectMode((v) => !v);
							setSelected(new Set());
						}}
						disabled={documents.length === 0}
					/>
					<Button
						size="sm"
						onClick={handleFileSelect}
						disabled={uploadMut.isPending}
					>
						{uploadMut.isPending ? (
							<>
								<Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
								{t("knowledge_uploading")}
							</>
						) : (
							<>
								<FileUp className="h-3.5 w-3.5 mr-1.5" />
								{t("knowledge_upload")}
							</>
						)}
					</Button>
				</div>
			</div>

			{uploadMut.isError && (
				<p className="text-sm text-destructive">
					{uploadMut.error instanceof Error
						? uploadMut.error.message
						: t("knowledge_upload_error")}
				</p>
			)}

			{isLoading ? (
				<div className="flex items-center justify-center py-12 text-muted-foreground">
					<Loader2 className="h-5 w-5 animate-spin mr-2" />
					{t("common:loading")}
				</div>
			) : documents.length === 0 && !search ? (
				<div className="flex flex-col items-center justify-center rounded-lg border border-dashed py-16 px-6">
					<div className="flex h-10 w-10 items-center justify-center rounded-lg bg-muted mb-4">
						<Inbox className="h-5 w-5 text-muted-foreground" />
					</div>
					<p className="text-sm font-medium mb-1">{t("knowledge_no_docs")}</p>
					<p className="text-sm text-muted-foreground text-center max-w-sm mb-4">
						{t("knowledge_no_docs_hint")}
					</p>
					<Button size="sm" variant="outline" onClick={handleFileSelect}>
						<FileUp className="h-3.5 w-3.5 mr-1.5" />
						{t("knowledge_upload")}
					</Button>
				</div>
			) : documents.length === 0 && search ? (
				<p className="text-sm text-muted-foreground py-8 text-center">
					{t("common:no_results")}
				</p>
			) : (
				<div className="rounded-lg border overflow-hidden">
					<Table>
						<TableHeader>
							<TableRow className="bg-muted/30">
								{selectMode && (
									<TableHead className="w-8">
										<Checkbox
											checked={
												selected.size === documents.length &&
												documents.length > 0
													? true
													: selected.size > 0
														? "indeterminate"
														: false
											}
											onCheckedChange={() => {
												if (selected.size === documents.length) {
													setSelected(new Set());
												} else {
													setSelected(new Set(documents.map((d) => d.id)));
												}
											}}
										/>
									</TableHead>
								)}
								<TableHead>{t("knowledge_col_document")}</TableHead>
								<TableHead className="w-28">
									{t("knowledge_col_status")}
								</TableHead>
								<TableHead className="w-24 text-right">
									{t("knowledge_col_chunks")}
								</TableHead>
								<TableHead className="w-36">
									{t("knowledge_col_uploaded")}
								</TableHead>
							</TableRow>
						</TableHeader>
						<TableBody>
							{documents.map((doc) => (
								<TableRow
									key={doc.id}
									className="cursor-pointer hover:bg-muted/50"
									onClick={() => {
										if (selectMode) {
											toggleSelect(doc.id);
										} else {
											navigate({
												to: "/knowledge-doc/$id",
												params: {
													id: doc.id,
												},
											});
										}
									}}
								>
									{selectMode && (
										<TableCell onClick={(e) => e.stopPropagation()}>
											<Checkbox
												checked={selected.has(doc.id)}
												onCheckedChange={() => toggleSelect(doc.id)}
											/>
										</TableCell>
									)}
									<TableCell>
										<div className="flex items-center gap-2 min-w-0">
											<FileText className="h-4 w-4 text-muted-foreground shrink-0" />
											<div className="min-w-0">
												<p className="text-sm font-medium truncate">
													{doc.title || doc.filename}
												</p>
												{doc.errorMessage && (
													<p className="text-xs text-destructive mt-0.5 truncate">
														{doc.errorMessage}
													</p>
												)}
											</div>
										</div>
									</TableCell>
									<TableCell>
										<ProcessingStatus state={doc.status} />
									</TableCell>
									<TableCell className="text-right tabular-nums text-sm">
										{doc.chunkCount > 0 ? doc.chunkCount : "—"}
									</TableCell>
									<TableCell className="text-sm text-muted-foreground">
										{doc.createdAt
											? doc.createdAt.toDate().toLocaleDateString("ru-RU", {
													day: "2-digit",
													month: "2-digit",
													year: "2-digit",
													hour: "2-digit",
													minute: "2-digit",
												})
											: "—"}
									</TableCell>
								</TableRow>
							))}
						</TableBody>
					</Table>
				</div>
			)}

			<BatchActionBar
				selectMode={selectMode}
				count={selected.size}
				total={documents.length}
				onSelectAll={() => setSelected(new Set(documents.map((d) => d.id)))}
				onClear={() => {
					setSelected(new Set());
					setSelectMode(false);
				}}
				onDelete={() => deleteMut.mutate([...selected])}
				isPending={deleteMut.isPending}
				entityLabel={t("common:document")}
			/>
		</div>
	);
}
